import { BankFormConfig } from "@components/utils/AddFundingProfileForm/types";
import { BANK_INFO_FORM_FIELDS, DEFAULT_CARD_TYPES } from "@constants";
import useAuthSession from "@hooks/useAuthSession";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { Currency } from "@types";
import { MemberSettingNameMode, MemberUniqueInfoCollection } from "kz-ui-sdk";
import { useMemo } from "react";

export const MEMBER_UNIQUE_INFO_FORM_FIELD_MAPPING = {
  [MemberUniqueInfoCollection.PAN]: "pan",
  [MemberUniqueInfoCollection.CPF]: "cpf",
  [MemberUniqueInfoCollection.CURP]: "curp",
  [MemberUniqueInfoCollection.DNI]: "pid",
  [MemberUniqueInfoCollection.CE]: "pid",
  [MemberUniqueInfoCollection.PEN_PAS]: "pid",
  [MemberUniqueInfoCollection.RUC]: "pid",
  [MemberUniqueInfoCollection.CC]: "pid",
  [MemberUniqueInfoCollection.NIT]: "pid",
  [MemberUniqueInfoCollection.COP_PAS]: "pid",
} as const;

// Compute available PID type options based on playerInfoCollections
const PID_TYPE_MAP: Record<MemberUniqueInfoCollection, string> = {
  [MemberUniqueInfoCollection.DNI]: "dni",
  [MemberUniqueInfoCollection.CE]: "ce",
  [MemberUniqueInfoCollection.PEN_PAS]: "pen_pas",
  [MemberUniqueInfoCollection.RUC]: "ruc",
  [MemberUniqueInfoCollection.CC]: "cc",
  [MemberUniqueInfoCollection.NIT]: "nit",
  [MemberUniqueInfoCollection.COP_PAS]: "cop_pas",
} as Record<MemberUniqueInfoCollection, string>;

export const useFundingProfileFormConfig = () => {
  const { playerInfoCollections = [], currency, nameMode } = useMemberSettings();
  const { fundingProfileSettings, legalName, self } = useAuthSession();

  const bankFormConfig = useMemo(() => {
    const baseFields = BANK_INFO_FORM_FIELDS[currency as Currency] ?? [];

    // 1) Unique-info fields handling:
    //    Do NOT filter out unsupported unique fields; keep them but set enabled=false
    const uniqueFieldNames = new Set<string>(Object.values(MEMBER_UNIQUE_INFO_FORM_FIELD_MAPPING));
    const mappingByNumber = MEMBER_UNIQUE_INFO_FORM_FIELD_MAPPING;
    const allowedUniqueFieldNames = new Set<string>(
      playerInfoCollections.map((c) => mappingByNumber[c]).filter((v) => !!v),
    );

    // 2) Apply server bank type mapping/options (primarily for INR upi/ifsc)
    const serverBankTypes = fundingProfileSettings?.supportedTypes?.bank?.type ?? [];
    const optionMap: Record<string, string> = { ifsc: "bank", upi: "upi" };
    const mappedBankTypeOptions = serverBankTypes.map((t) => optionMap[t] ?? t);
    const hasServerTypes = mappedBankTypeOptions.length > 0;

    // Build config with ordering
    const intermediateConfig: BankFormConfig = baseFields.reduce(
      (acc: any, field, index) => {
        if (Array.isArray(field)) {
          const [fieldName, options] = field;
          const isBankType = fieldName === "bankTypeOption";
          const isUniqueButNotAllowed = uniqueFieldNames.has(fieldName) && !allowedUniqueFieldNames.has(fieldName);
          acc[fieldName] = {
            enabled: !isUniqueButNotAllowed,
            sortPriority: index,
            options: isBankType && hasServerTypes ? mappedBankTypeOptions : options.options,
          };
        } else {
          const fieldName = field;
          const isUniqueButNotAllowed = uniqueFieldNames.has(fieldName) && !allowedUniqueFieldNames.has(fieldName);
          acc[fieldName] = {
            enabled: !isUniqueButNotAllowed,
            sortPriority: index,
          };
        }
        return acc;
      },
      {} as Record<string, { enabled: boolean; sortPriority: number; options?: string[] }>,
    );

    // If server restricts bank types, toggle subfields accordingly
    if (hasServerTypes) {
      const allowIfsc = mappedBankTypeOptions.includes("bank");
      if (intermediateConfig["ifsc"]) {
        intermediateConfig["ifsc"].enabled = !!allowIfsc;
      }
      if (intermediateConfig["bankTypeOption"]) {
        // Hide selector if only one option remains
        if (mappedBankTypeOptions.length <= 1) {
          intermediateConfig["bankTypeOption"].enabled = false;
        }
        intermediateConfig["bankTypeOption"].options = mappedBankTypeOptions;
      }
    }

    //    - name -> fullName or firstName/lastName depending on nameMode
    if (intermediateConfig["name"]) {
      const nameEnabled = intermediateConfig["name"].enabled && !legalName;
      // nameMode from member settings decides the split
      if (nameMode === MemberSettingNameMode.FullInFirst) {
        intermediateConfig["fullName"] = {
          enabled: !!nameEnabled,
          sortPriority: intermediateConfig["name"].sortPriority,
        };
      }
      if (nameMode === MemberSettingNameMode.FirstLast) {
        intermediateConfig["firstName"] = {
          enabled: !!nameEnabled,
          sortPriority: intermediateConfig["name"].sortPriority,
        };
        intermediateConfig["lastName"] = {
          enabled: !!nameEnabled,
          sortPriority: (intermediateConfig["name"].sortPriority ?? 0) + 1,
        };
      }
      delete intermediateConfig["name"];
    }

    // 4) Disable document inputs if already collected in member info (if available)
    const memberInfo = self?.member?.info || {};
    const pidCollected = !!(
      memberInfo?.dni ||
      memberInfo?.ce ||
      memberInfo?.pen_pas ||
      memberInfo?.ruc ||
      memberInfo?.cc ||
      memberInfo?.nit ||
      memberInfo?.cop_pas
    );

    const docsCollected = {
      pan: !!memberInfo?.pan,
      cpf: !!memberInfo?.cpf,
      curp: !!memberInfo?.curp,
      pid: pidCollected,
    } as const;
    if (intermediateConfig["pan"]) {
      intermediateConfig["pan"].enabled = intermediateConfig["pan"].enabled && !docsCollected.pan;
    }
    if (intermediateConfig["cpf"]) {
      intermediateConfig["cpf"].enabled = intermediateConfig["cpf"].enabled && !docsCollected.cpf;
    }
    if (intermediateConfig["curp"]) {
      intermediateConfig["curp"].enabled = intermediateConfig["curp"].enabled && !docsCollected.curp;
    }
    if (intermediateConfig["pid"]) {
      intermediateConfig["pid"].enabled = intermediateConfig["pid"].enabled && !docsCollected.pid;

      const allPidTypeOptions = [
        // PEN
        { id: "dni", name: "dni" },
        { id: "ce", name: "ce" },
        { id: "pen_pas", name: "pen_pas" },
        { id: "ruc", name: "ruc" },
        // COP
        { id: "cc", name: "cc" },
        { id: "nit", name: "nit" },
        { id: "cop_pas", name: "cop_pas" },
      ];

      const availablePidTypes = playerInfoCollections
        .filter((collection) => PID_TYPE_MAP[collection])
        .map((collection) => PID_TYPE_MAP[collection]);

      intermediateConfig["pid"].pidTypeOptions = allPidTypeOptions.filter((option) =>
        availablePidTypes.includes(option.id),
      );
    }

    // 5) Country-specific helpers
    if ((currency as Currency) === Currency.INR) {
      intermediateConfig.paymentMethodOptions = mappedBankTypeOptions; // ["upi","bank"] typically
      intermediateConfig.labelsByMethod = {
        upi: { accountNumber: "upi id", placeholder: "username@bankname" },
        bank: { accountNumber: "account number", placeholder: "1234 5678 9012 3456" },
      };
      intermediateConfig.ifscToggleAvailable = mappedBankTypeOptions.includes("bank");
    }

    // 6) Card type options from server (PEN, COP)
    if ([Currency.PEN, Currency.COP].includes(currency as Currency) && intermediateConfig["cardType"]) {
      const serverCardTypes = fundingProfileSettings?.supportedTypes?.bank?.cardTypes ?? [];
      intermediateConfig["cardType"].cardTypeOptions =
        serverCardTypes.length > 0 ? serverCardTypes : DEFAULT_CARD_TYPES;
    }

    return intermediateConfig;
  }, [currency, playerInfoCollections, fundingProfileSettings, legalName, nameMode, self]);

  return {
    bankFormConfig,
  };
};
